import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ConfirmProvider } from "@/components/useConfirm";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import ManagerLogin from "@/pages/ManagerLogin";
import AccessRestricted from "@/pages/AccessRestricted";
import { ManagerLayout } from "@/components/ManagerLayout";
import ManagerDashboard from "@/pages/manager/ManagerDashboard";
import ManagerRoster from "@/pages/manager/ManagerRoster";
import ManagerLeave from "@/pages/manager/ManagerLeave";
import ManagerLeaveHeatmap from "@/pages/manager/ManagerLeaveHeatmap";
import ManagerTimesheets from "@/pages/manager/ManagerTimesheets";
import ManagerActivity from "@/pages/manager/ManagerActivity";
import ManagerReports from "@/pages/manager/ManagerReports";
import ManagerIncidents from "@/pages/manager/ManagerIncidents";
import ManagerSiteIncidents from "@/pages/manager/ManagerSiteIncidents";
import ManagerEmployeeDocuments from "@/pages/manager/ManagerEmployeeDocuments";
import ManagerExpenses from "@/pages/manager/ManagerExpenses";
import AdminSiteIncidents from "@/pages/AdminSiteIncidents";
import AdminExpenses from "@/pages/AdminExpenses";
import AdminSalesDashboard from "@/pages/AdminSalesDashboard";
import FoodWastage from "@/pages/FoodWastage";
import OrderingSheet from "@/pages/OrderingSheet";
import Expenses from "@/pages/Expenses";
import Recipes from "@/pages/Recipes";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import Timesheet from "@/pages/Timesheet";
import Leave from "@/pages/Leave";
import MyIncidents from "@/pages/MyIncidents";
import Payslips from "@/pages/Payslips";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminEmployees from "@/pages/AdminEmployees";
import AdminEmployeeDetail from "@/pages/AdminEmployeeDetail";
import AdminTimesheets from "@/pages/AdminTimesheets";
import AdminLeave from "@/pages/AdminLeave";
import AdminAudit from "@/pages/AdminAudit";
import AdminXero from "@/pages/AdminXero";
import AdminDocuments from "@/pages/AdminDocuments";
import AdminVisas from "@/pages/AdminVisas";
import AdminTrials from "@/pages/AdminTrials";
import AdminSettings from "@/pages/AdminSettings";
import DeveloperApply from "@/pages/DeveloperApply";
import VarianceReport from "@/pages/VarianceReport";
import { InstallPrompt } from "@/components/InstallPrompt";

function RoleHome() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "ADMIN" || user.role === "SUPERADMIN")
    return <Navigate to="/admin" replace />;
  if (user.role === "MANAGER")
    return <Navigate to="/admin/manager/dashboard" replace />;
  return <Navigate to="/clock" replace />;
}

function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" closeButton />
        <Routes>
          {/* Public auth routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/admin/login" element={<ManagerLogin />} />
          <Route path="/access-restricted" element={<AccessRestricted />} />
          <Route path="/developer/apply" element={<DeveloperApply />} />

          {/* Role-based landing */}
          <Route path="/" element={<RoleHome />} />

          {/* Employee routes */}
          <Route
            path="/clock"
            element={
              <ProtectedRoute roles={["EMPLOYEE"]}>
                <Layout><EmployeeDashboard /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/timesheet"
            element={
              <ProtectedRoute roles={["EMPLOYEE"]}>
                <Layout><Timesheet /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave"
            element={
              <ProtectedRoute roles={["EMPLOYEE"]}>
                <Layout><Leave /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/payslips"
            element={
              <ProtectedRoute>
                <Payslips />
              </ProtectedRoute>
            }
          />
          <Route
            path="/incidents"
            element={
              <ProtectedRoute roles={["EMPLOYEE"]}>
                <Layout><MyIncidents /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><AdminDashboard /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Manager routes — accessible by MANAGER (site-locked),
              ADMIN, and SUPERADMIN (cross-site via site filter). */}
          <Route
            path="/admin/manager/dashboard"
            element={
              <ProtectedRoute roles={["MANAGER", "ADMIN", "SUPERADMIN"]}>
                <ManagerLayout><ManagerDashboard /></ManagerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manager/roster"
            element={
              <ProtectedRoute roles={["MANAGER", "ADMIN", "SUPERADMIN"]}>
                <ManagerLayout><ManagerRoster /></ManagerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manager/leave"
            element={
              <ProtectedRoute roles={["MANAGER", "ADMIN", "SUPERADMIN"]}>
                <ManagerLayout><ManagerLeave /></ManagerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manager/leave/heatmap"
            element={
              <ProtectedRoute roles={["MANAGER", "ADMIN", "SUPERADMIN"]}>
                <ManagerLayout><ManagerLeaveHeatmap /></ManagerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manager/timesheets"
            element={
              <ProtectedRoute roles={["MANAGER", "ADMIN", "SUPERADMIN"]}>
                <ManagerLayout><ManagerTimesheets /></ManagerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manager/incidents"
            element={
              <ProtectedRoute roles={["MANAGER", "ADMIN", "SUPERADMIN"]}>
                <ManagerLayout><ManagerIncidents /></ManagerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manager/site-incidents"
            element={
              <ProtectedRoute roles={["MANAGER", "ADMIN", "SUPERADMIN"]}>
                <ManagerLayout><ManagerSiteIncidents /></ManagerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manager/documents"
            element={
              <ProtectedRoute roles={["MANAGER", "ADMIN", "SUPERADMIN"]}>
                <ManagerLayout><ManagerEmployeeDocuments /></ManagerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manager/expenses"
            element={
              <ProtectedRoute roles={["MANAGER", "ADMIN", "SUPERADMIN"]}>
                <ManagerLayout><ManagerExpenses /></ManagerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/site-incidents"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><AdminSiteIncidents /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/expenses"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><AdminExpenses /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses"
            element={
              <ProtectedRoute>
                <Layout><Expenses /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recipes"
            element={
              <ProtectedRoute>
                <Layout><Recipes /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wastage"
            element={
              <ProtectedRoute>
                <Layout><FoodWastage /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ordering"
            element={
              <ProtectedRoute>
                <Layout><OrderingSheet /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/wastage"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><FoodWastage /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ordering"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><OrderingSheet /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manager/wastage"
            element={
              <ProtectedRoute roles={["MANAGER", "ADMIN", "SUPERADMIN"]}>
                <ManagerLayout><FoodWastage /></ManagerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manager/ordering"
            element={
              <ProtectedRoute roles={["MANAGER", "ADMIN", "SUPERADMIN"]}>
                <ManagerLayout><OrderingSheet /></ManagerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales"
            element={
              <ProtectedRoute>
                <Layout><AdminSalesDashboard /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/sales"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><AdminSalesDashboard /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manager/sales"
            element={
              <ProtectedRoute roles={["MANAGER", "ADMIN", "SUPERADMIN"]}>
                <ManagerLayout><AdminSalesDashboard /></ManagerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manager/reports/activity"
            element={
              <ProtectedRoute roles={["MANAGER", "ADMIN", "SUPERADMIN"]}>
                <ManagerLayout><ManagerActivity /></ManagerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manager/reports"
            element={
              <ProtectedRoute roles={["MANAGER", "ADMIN", "SUPERADMIN"]}>
                <ManagerLayout><ManagerReports /></ManagerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manager/variance"
            element={
              <ProtectedRoute roles={["MANAGER", "ADMIN", "SUPERADMIN"]}>
                <ManagerLayout><VarianceReport /></ManagerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/variance"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><VarianceReport /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/employees"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><AdminEmployees /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/employees/:id"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><AdminEmployeeDetail /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/timesheets"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><AdminTimesheets /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/leave"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><AdminLeave /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><AdminAudit /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/xero"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><AdminXero /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/documents"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><AdminDocuments /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/visas"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><AdminVisas /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/trials"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><AdminTrials /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manager/trials"
            element={
              <ProtectedRoute roles={["MANAGER", "ADMIN", "SUPERADMIN"]}>
                <ManagerLayout><AdminTrials /></ManagerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute roles={["ADMIN", "SUPERADMIN"]}>
                <Layout><AdminSettings /></Layout>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <InstallPrompt />
      </BrowserRouter>
      </ConfirmProvider>
    </AuthProvider>
  );
}

export default App;
